import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  TabProps,
  Tabs,
  VisuallyHidden,
  useBoolean,
} from '@chakra-ui/react';
import { CustomPageOption, DownloadOption } from 'constants/download';
import { TAB_PANEL_MIN_HEIGHT } from 'constants/editor';
import { UPGRADE_REDIRECT_BIZ_LINK, UPGRADE_REDIRECT_LINK } from 'constants/links';
import { UPGRADE } from 'constants/mixpanel';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { parseCustomPageRangeInput } from 'modules/Editor/components/CustomPageRange/CustomPageRange.helpers';
import { useFocus } from 'modules/Editor/components/Focus';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectDownloadModalTrigger, selectIsDownloadModalOpen } from 'modules/Editor/store/editorSettingsSelector';
import { setDownloadModal } from 'modules/Editor/store/editorSettingsSlice';
import { DownloadModalTrigger } from 'modules/Editor/store/editorSettingsSlice.types';
import { selectPageOrder } from 'modules/Editor/store/infographSelector';
import { UserTypes, useUser } from 'modules/User';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';

import { EmbedPanel } from '../EmbedPanel';
import PreviewPanel from '../PreviewPanel';
import PublishPanel from '../PublishPanel';
import SharePanelLegacy from '../SharePanel';
import { SharePanel } from '../SharePanel/index';
import { CustomPageOptionSelect } from './CustomPageOptionSelect';
import { DownloadButton } from './DownloadButton';
import { DefaultTabNumber } from './DownloadModal.types';
import { DownloadOptionSelect } from './DownloadOptionSelect';
import { UpgradeButton } from './UpgradeButton';

const tabStyles: TabProps = {
  fontWeight: 'semibold',
  fontSize: 'sm',
  px: '14px',
  pt: '5',
  pb: '2',
  _selected: { color: 'upgrade.blue.500', borderBottom: '2px solid' },
};

export const DownloadModal = () => {
  const isDownloadModalOpen = useAppSelector(selectIsDownloadModalOpen);
  const downloadModalTrigger = useAppSelector(selectDownloadModalTrigger);
  const dispatch = useAppDispatch();
  const { closeAccessibilityMenuRef } = useFocus();

  const finalFocusRef =
    downloadModalTrigger === DownloadModalTrigger.AccessibilityMenu && closeAccessibilityMenuRef
      ? closeAccessibilityMenuRef
      : undefined;

  const onClose = () => dispatch(setDownloadModal({ isOpen: false }));

  return (
    <Modal finalFocusRef={finalFocusRef} isOpen={isDownloadModalOpen} onClose={onClose} size='md'>
      <ModalOverlay />
      <DownloadModalContent onClose={onClose} />
    </Modal>
  );
};

const modalBodyTestId = 'download-modal-body';
const modalCloseButtonTestId = 'close-download-modal-button';

type DownloadModalContentProps = {
  onClose: () => void;
};

const DownloadModalContent = ({ onClose }: DownloadModalContentProps) => {
  const { venngageE2 } = useVenngageE1Gate();
  const { trackChameleon } = useChameleon();

  const downloadModalTrigger = useAppSelector(selectDownloadModalTrigger);
  const pageOrder = useAppSelector(selectPageOrder);

  const { t } = useTranslation('common_navbar');

  let initialTab =
    downloadModalTrigger === DownloadModalTrigger.ShareButton ? DefaultTabNumber.Share : DefaultTabNumber.Download;
  // When DownloadModal closes, downloadModalTrigger sets to undefined, reset initialTab to -1, this ensures the useEffect to set tabIndex to initialTab would be triggered when open modal again
  if (!downloadModalTrigger) initialTab = DefaultTabNumber.Initial;

  const [tabIndex, setTabIndex] = useState<DefaultTabNumber>(initialTab);

  const handleTabsChange = (index: DefaultTabNumber) => {
    setTabIndex(index);
  };

  const { data: userInfo } = useUser();
  const userType = userInfo?.user_type;
  const isFreeUser = userType === UserTypes.Free;
  const isPremiumUser = userType === UserTypes.Premium;

  // TODO: Need to refactor this VenngageE2 global variable
  // Not gate PNG or PDF export on preview link when VenngageE2.experiments is not available
  const gatePNGExport = isFreeUser && !!venngageE2?.experiments;
  const gatePremiumPDFExport =
    isPremiumUser && !!venngageE2?.experiments && venngageE2?.experiments.gatePdfPremiumDownload;
  const gatePDFExport = (isFreeUser || gatePremiumPDFExport) && !!venngageE2?.experiments;

  // Add team share button
  // TODO: This will be refactored when we do UI improvement
  const enableTeamShareFeature = !!venngageE2?.experiments?.teamshare;

  const [downloadOption, setDownloadOption] = useState<DownloadOption>(DownloadOption.InteractivePDF);
  const [customPageRangeOption, setCustomPageRangeOption] = useState(CustomPageOption.All);
  const [selectedPageNumbers, setSelectedPageNumbers] = useState<number[]>([]);
  const [isValidRange, setIsValidRange] = useBoolean(true);

  const downloadOptionHandler = (option: DownloadOption) => setDownloadOption(option);

  // This useEffect is used to update the default download option for premium users as the initial render userType is undefined, gatePDFExport is false, when userType is fetched, gatePDFExport would then turn true
  useEffect(() => {
    if (gatePDFExport) downloadOptionHandler(DownloadOption.PNG);
  }, [gatePDFExport]);

  // This useEffect is to handle an edge case when free users upgrade and come back to the modal, and publish panel is hidden then
  useEffect(() => {
    if (userType !== UserTypes.Free && tabIndex === DefaultTabNumber.Publish) {
      setTabIndex(DefaultTabNumber.Share);
    }
  }, [userType, tabIndex]);

  const clickUpgradeHandler = (featureProp: string) => {
    // Set mixpanel_feature session storage item used by `Analytics: Upgrade` event
    setAnalyticsUpgradeFeatureProp(featureProp);
    const redirectUrl =
      featureProp === UPGRADE.feature.DownloadBusiness ? UPGRADE_REDIRECT_BIZ_LINK : UPGRADE_REDIRECT_LINK;
    window.open(redirectUrl, '_self');
    Mixpanel.track(UPGRADE.eventName, {
      Feature: featureProp,
    });
    trackChameleon(UPGRADE.eventName);
  };

  const handleSelectedRanges = (value: string) => {
    if (value === '') {
      setSelectedPageNumbers([]);
      setIsValidRange.on();
      return;
    }

    try {
      const selectedPages = parseCustomPageRangeInput(value, pageOrder.length);

      setSelectedPageNumbers(selectedPages);
      setIsValidRange.on();
    } catch (err) {
      // Validation fail
      setIsValidRange.off();
    }
  };

  const debounceHandleSelectedPages = useDebouncedCallback(handleSelectedRanges, 300);

  const handleCustomPageRangeOption = (option: CustomPageOption) => {
    if (option === CustomPageOption.All && selectedPageNumbers.length > 0) {
      setSelectedPageNumbers([]);
    }

    setCustomPageRangeOption(option);
  };

  const handleCustomPageRangeInput = (input: string) => {
    debounceHandleSelectedPages(input);
  };

  const isMultipage = pageOrder.length > 1;
  const showCustomPageSelect = isMultipage && !isFreeUser;
  const showControlSharePanel = isFreeUser;

  return (
    <ModalContent>
      <ModalCloseButton data-testid={modalCloseButtonTestId} />
      <Tabs index={tabIndex} onChange={handleTabsChange} isLazy>
        <TabList>
          <Flex ml={2} flexWrap={'wrap'}>
            <VisuallyHidden>
              <Tab {...tabStyles}>{t('modals.preview-panel.title-preview')}</Tab>
            </VisuallyHidden>
            <Tab {...tabStyles}>{t('modals.share-panel.title-share')}</Tab>
            <Tab {...tabStyles}>{t('modals.download.title-download')}</Tab>
            <Tab {...tabStyles}>{t('modals.embed')}</Tab>
            {showControlSharePanel && <Tab {...tabStyles}>{t('modals.publish-panel.title-publish')}</Tab>}
          </Flex>
        </TabList>
        <TabPanels>
          <TabPanel display='flex' flexDirection='column' pt='5' px='6' pb='7' minH={TAB_PANEL_MIN_HEIGHT}>
            <Flex flexDir={'column'} justifyContent={'space-between'}>
              <PreviewPanel
                onUpgrade={clickUpgradeHandler.bind(null, UPGRADE.feature.Preview)}
                onClose={onClose}
                onUpgradeLock={clickUpgradeHandler.bind(null, UPGRADE.feature.Preview)}
                tabIndex={tabIndex}
              />
            </Flex>
          </TabPanel>
          <TabPanel display='flex' flexDirection='column' pt='5' px='6' pb='7' minH={TAB_PANEL_MIN_HEIGHT}>
            <Flex>
              {enableTeamShareFeature ? (
                <Flex flex='1'>
                  <SharePanel onUpgrade={clickUpgradeHandler} onClose={onClose} tabIndex={tabIndex}></SharePanel>
                </Flex>
              ) : (
                <SharePanelLegacy
                  userType={userType}
                  handleClickUpgrade={clickUpgradeHandler.bind(null, UPGRADE.feature.Share)}
                />
              )}
            </Flex>
          </TabPanel>
          <TabPanel pt='5' px='6' pb='7' minH={TAB_PANEL_MIN_HEIGHT}>
            <ModalBody data-testid={modalBodyTestId} p={0} color={'font.500'}>
              <Flex flexDir='column' borderRadius='base' mb={4} alignItems='start'>
                {showCustomPageSelect && (
                  <CustomPageOptionSelect
                    customPageRangeOption={customPageRangeOption}
                    handleCustomPageRangeOption={handleCustomPageRangeOption}
                    handleCustomPageRangeInput={handleCustomPageRangeInput}
                    isValidRange={isValidRange}
                  />
                )}
                <DownloadOptionSelect
                  downloadOption={downloadOption}
                  handleDownloadOption={downloadOptionHandler}
                  gatePNGExport={gatePNGExport}
                  gatePDFExport={gatePDFExport}
                  handleClickUpgradePremium={clickUpgradeHandler.bind(null, UPGRADE.feature.DownloadPremium)}
                  handleClickUpgradeBusiness={clickUpgradeHandler.bind(null, UPGRADE.feature.DownloadBusiness)}
                />
              </Flex>
            </ModalBody>
            <ModalFooter p='0' mt='4'>
              {isFreeUser ? (
                <UpgradeButton handleClickUpgrade={clickUpgradeHandler.bind(null, UPGRADE.feature.DownloadButton)} />
              ) : (
                <DownloadButton
                  downloadOption={downloadOption}
                  shouldDisable={!isValidRange}
                  selectedPageNumbers={selectedPageNumbers}
                />
              )}
            </ModalFooter>
          </TabPanel>
          {/* start of embed */}
          <TabPanel pt='5' px='6' pb='7' minH={TAB_PANEL_MIN_HEIGHT}>
            {/* implementation for the embed */}
            <Flex>
              <EmbedPanel handleClickUpgrade={clickUpgradeHandler.bind(null, UPGRADE.feature.Embed)} />
            </Flex>
          </TabPanel>
          {/* end of line */}
          {isFreeUser && (
            <TabPanel pt='5' px='6' pb='7' minH={TAB_PANEL_MIN_HEIGHT}>
              <PublishPanel handleClickUpgrade={clickUpgradeHandler.bind(null, UPGRADE.feature.FreeShareModal)} />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </ModalContent>
  );
};
